import { transformStringToWhatsapp } from './transform-string-to-url';

type WhatsappPhoneInput = string | number;

interface GetWhatsappLinkParams {
  phone: WhatsappPhoneInput;
  text: string;
  /**
   * DDI a ser aplicado quando o número vier apenas com DDD+celular (11 dígitos).
   * Default: 55 (Brasil)
   */
  defaultCountryCode?: string;
}

function normalizeDigits(value: WhatsappPhoneInput): string {
  return String(value).replaceAll(/\D/g, '');
}

function normalizeCountryCode(value: string): string {
  const digits = value.replaceAll(/\D/g, '');

  return digits.length === 0 ? '55' : digits;
}

/**
 * Gera o link do WhatsApp (wa.me) com telefone e texto normalizados.
 * Mantém a função pura (sem side effects) para poder ser usada em qualquer app.
 */
export function getWhatsappLink({
  phone,
  text,
  defaultCountryCode = '55',
}: GetWhatsappLinkParams): string {
  const rawPhoneDigits = normalizeDigits(phone);
  const countryCode = normalizeCountryCode(defaultCountryCode);

  // Regra prática usada nos apps: se vier apenas com 11 dígitos (DDD + celular), prefixa com DDI.
  const phoneWithCountryCode =
    rawPhoneDigits.length === 11
      ? `${countryCode}${rawPhoneDigits}`
      : rawPhoneDigits;

  const encodedText = transformStringToWhatsapp(text);

  return `https://wa.me/${phoneWithCountryCode}?text=${encodedText}`;
}

interface OpenWhatsappChatParams extends GetWhatsappLinkParams {
  target?: '_blank' | '_self';
}

/**
 * Abre uma conversa no WhatsApp no browser (via window.open), montando o link
 * com telefone e texto normalizados.
 *
 * Retorna true quando o `window.open` foi chamado, e false quando não foi
 * possível (ex.: SSR ou parâmetros inválidos).
 */
export function openWhatsappChat({
  phone,
  text,
  defaultCountryCode,
  target = '_blank',
}: OpenWhatsappChatParams): boolean {
  if (typeof window === 'undefined') return false;

  const phoneDigits = normalizeDigits(phone);

  if (phoneDigits.length < 11) return false;

  const url = getWhatsappLink({ phone, text, defaultCountryCode });

  window.open(url, target, 'noreferrer');

  return true;
}
